/**
 * ExternalRelease API functions.
 */

import apiClient from './client';
import type {
  ExternalRelease,
  ExternalReleaseDetail,
  ExternalReleaseCreate,
  ExternalReleaseUpdate,
  ExternalReleaseQueryParams,
  ExternalReleaseFile,
  ExternalReleaseFileCreate,
  PaginatedResponse,
  OperationLog,
  ProjectFile,
} from '@/types';

export const listExternalReleases = async (
  params?: ExternalReleaseQueryParams
): Promise<PaginatedResponse<ExternalRelease>> => {
  const response = await apiClient.get<PaginatedResponse<ExternalRelease>>(
    '/external-releases/',
    { params }
  );
  return response.data;
};

export const getExternalRelease = async (id: number): Promise<ExternalReleaseDetail> => {
  const response = await apiClient.get<ExternalReleaseDetail>(`/external-releases/${id}/`);
  return response.data;
};

export const createExternalRelease = async (
  data: ExternalReleaseCreate | Record<string, unknown>
): Promise<ExternalReleaseDetail> => {
  const response = await apiClient.post<ExternalReleaseDetail>('/external-releases/', data);
  return response.data;
};

export const updateExternalRelease = async (
  id: number,
  data: ExternalReleaseUpdate | Record<string, unknown>
): Promise<ExternalReleaseDetail> => {
  const response = await apiClient.put<ExternalReleaseDetail>(`/external-releases/${id}/`, data);
  return response.data;
};

export const patchExternalRelease = async (
  id: number,
  data: Record<string, unknown>
): Promise<ExternalReleaseDetail> => {
  const response = await apiClient.patch<ExternalReleaseDetail>(`/external-releases/${id}/`, data);
  return response.data;
};

export const deleteExternalRelease = async (id: number): Promise<void> => {
  await apiClient.delete(`/external-releases/${id}/`);
};

export const addExternalReleaseFile = async (
  id: number,
  data: ExternalReleaseFileCreate
): Promise<ExternalReleaseFile> => {
  const response = await apiClient.post<ExternalReleaseFile>(
    `/external-releases/${id}/files/`,
    data
  );
  return response.data;
};

export const deleteExternalReleaseFile = async (
  id: number,
  fileId: number
): Promise<void> => {
  await apiClient.delete(`/external-releases/${id}/files/${fileId}/`);
};

export const deleteExternalReleaseNote = async (id: number): Promise<void> => {
  await apiClient.delete(`/external-releases/${id}/release-note/`);
};

export const deleteExternalReleaseTestReport = async (id: number): Promise<void> => {
  await apiClient.delete(`/external-releases/${id}/test-report/`);
};

export const failExternalReleaseNoteUpload = async (id: number): Promise<void> => {
  await apiClient.patch(`/external-releases/${id}/release-note/fail/`);
};

export const failExternalReleaseTestReportUpload = async (id: number): Promise<void> => {
  await apiClient.patch(`/external-releases/${id}/test-report/fail/`);
};

export const getExternalReleaseProjectFiles = async (
  projectId: number,
  fileTypeId: number,
  excludeErId?: number
): Promise<ProjectFile[]> => {
  const params: Record<string, number> = {
    project_id: projectId,
    file_type_id: fileTypeId,
  };
  if (excludeErId) params.exclude_er_id = excludeErId;
  const response = await apiClient.get<ProjectFile[]>(
    '/external-releases/project-files/',
    { params }
  );
  return response.data;
};

export const getExternalReleaseLogs = async (
  id: number
): Promise<{ results: OperationLog[] }> => {
  const response = await apiClient.get<{ results: OperationLog[] }>(
    `/external-releases/${id}/logs/`
  );
  return response.data;
};

export const updateExternalReleaseUploadStatus = async (
  id: number,
  uploadStatus: 'uploading' | 'done' | 'failed',
  reason?: string
): Promise<{ upload_status: string }> => {
  const response = await apiClient.patch<{ upload_status: string }>(
    `/external-releases/${id}/upload-status/`,
    { upload_status: uploadStatus, ...(reason ? { reason } : {}) }
  );
  return response.data;
};

export const completeExternalReleaseFileUpload = async (
  id: number,
  fileId: number,
  data: { file_url: string; object_key: string; file_size: number; checksum: string; file_name?: string; upload_status?: string; upload_started_at?: string | null; upload_finished_at?: string | null; avg_speed_mbps?: number | null }
): Promise<ExternalReleaseFile> => {
  const response = await apiClient.patch<ExternalReleaseFile>(
    `/external-releases/${id}/files/${fileId}/upload-complete/`,
    data
  );
  return response.data;
};

export const failExternalReleaseFileUpload = async (
  id: number,
  fileId: number
): Promise<ExternalReleaseFile> => {
  const response = await apiClient.patch<ExternalReleaseFile>(
    `/external-releases/${id}/files/${fileId}/upload-complete/`,
    { upload_status: 'failed' }
  );
  return response.data;
};

export const startExternalReleaseFileUpload = async (
  id: number,
  fileId: number,
  status: 'queued' | 'uploading'
): Promise<void> => {
  await apiClient.patch(
    `/external-releases/${id}/files/${fileId}/upload-complete/`,
    { upload_status: status }
  );
};

export const heartbeatExternalReleaseFile = async (
  id: number,
  fileId: number,
  percent?: number,
): Promise<void> => {
  await apiClient.patch(`/external-releases/${id}/files/${fileId}/heartbeat/`, percent !== undefined ? { percent } : {});
};
